import { useQuery } from '@tanstack/react-query';
import { getClientPortalBillingDashboard } from 'services/billingServices';
import { CLIENT_TRUST_LEDGER_QUERY_KEY } from './useTrustLedgerQuery';

export const RETAINER_BILLING_DASHBOARD_QUERY_KEY = 'retainer-billing-dashboard';

export const invalidateClientRetainerBillingQueries = (queryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: [RETAINER_BILLING_DASHBOARD_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [CLIENT_TRUST_LEDGER_QUERY_KEY] }),
  ]);

const extractListSection = (section, { legacyUnpaidKey, legacyViewAllKey, raw }) => {
  if (Array.isArray(section)) {
    return {
      items: section,
      unpaid_count: raw?.[legacyUnpaidKey] ?? 0,
      view_all: raw?.[legacyViewAllKey] ?? false,
    };
  }

  if (section && typeof section === 'object') {
    return {
      items: Array.isArray(section.items) ? section.items : [],
      unpaid_count: section.unpaid_count ?? 0,
      view_all: section.view_all ?? false,
    };
  }

  return { items: [], unpaid_count: 0, view_all: false };
};

const normalizeRetainerSummary = (raw) => {
  const summary = raw?.summary;

  if (summary && typeof summary === 'object') {
    return {
      running_trust_balance: summary.running_trust_balance,
      designated_as_temp_trust_funds: summary.designated_as_temp_trust_funds,
      outstanding_balance: summary.outstanding_balance,
      outstanding_past_due: summary.outstanding_past_due,
      outstanding_current_due: summary.outstanding_current_due,
      outstanding_future_due: summary.outstanding_future_due,
    };
  }

  return {
    running_trust_balance: raw?.running_trust_balance,
    designated_as_temp_trust_funds: raw?.designated_as_temp_trust_funds,
    outstanding_balance: raw?.outstanding_balance,
    outstanding_past_due: raw?.outstanding_past_due,
    outstanding_current_due: raw?.outstanding_current_due,
    outstanding_future_due: raw?.outstanding_future_due,
  };
};

const hasDisplayableRetainerSummary = (summary) => {
  if (!summary || typeof summary !== 'object') return false;

  return [
    summary.running_trust_balance,
    summary.designated_as_temp_trust_funds,
    summary.outstanding_balance,
    summary.outstanding_past_due,
    summary.outstanding_current_due,
    summary.outstanding_future_due,
  ].some((value) => value !== undefined && value !== null && !Number.isNaN(Number(value)));
};

const resolveSummaryCardsVisible = (sectionsVisible, summary) => {
  if (sectionsVisible?.summary_cards === false) return false;
  if (sectionsVisible?.summary_cards === true) return true;
  return hasDisplayableRetainerSummary(summary);
};

const resolveRetainerSectionFlag = (explicitValue, derivedValue) => {
  if (explicitValue === false) return false;
  if (explicitValue === true) return true;
  return derivedValue;
};

/**
 * Derives retainer section visibility when the API omits flags.
 * Retainer PR/invoices use array length (not unpaid counts). Flat-fee uses API passthrough.
 *
 * @param {object} rawSectionsVisible
 * @param {{ paymentRequestItems: object[], invoiceItems: object[], summary: object }} sections
 * @returns {object}
 */
export const resolveRetainerSectionVisibility = (rawSectionsVisible, { paymentRequestItems, invoiceItems, summary }) => ({
  summary_cards: resolveSummaryCardsVisible(rawSectionsVisible, summary),
  payment_requests: resolveRetainerSectionFlag(rawSectionsVisible?.payment_requests, paymentRequestItems.length > 0),
  invoices: resolveRetainerSectionFlag(rawSectionsVisible?.invoices, invoiceItems.length > 0),
  trust_ledger: resolveRetainerSectionFlag(rawSectionsVisible?.trust_ledger, true),
});

/**
 * Normalizes retainer dashboard API response to the shape consumed by shared section components.
 *
 * @param {object} raw - Raw API response body
 * @returns {object} Normalized dashboard data
 */
export const normalizeRetainerDashboardResponse = (raw = {}) => {
  const paymentRequestsSection = extractListSection(raw.payment_request ?? raw.payment_requests, {
    legacyUnpaidKey: 'total_unpaid_payment_requests_count',
    legacyViewAllKey: 'view_more_payment_request',
    raw,
  });

  const invoicesSection = extractListSection(raw.invoice ?? raw.invoices, {
    legacyUnpaidKey: 'total_unpaid_invoices_count',
    legacyViewAllKey: 'view_more_invoices',
    raw,
  });

  const summary = normalizeRetainerSummary(raw);
  const sectionsVisible = resolveRetainerSectionVisibility(raw.sections_visible, {
    paymentRequestItems: paymentRequestsSection.items,
    invoiceItems: invoicesSection.items,
    summary,
  });

  return {
    ...raw,
    case_name: raw.case_name,
    sections_visible: sectionsVisible,
    summary,
    payment_requests: paymentRequestsSection.items,
    payment_requests_unpaid_count: paymentRequestsSection.unpaid_count,
    payment_requests_view_all: paymentRequestsSection.view_all,
    invoices: invoicesSection.items,
    invoices_unpaid_count: invoicesSection.unpaid_count,
    invoices_view_all: invoicesSection.view_all,
  };
};

export const useRetainerBillingDashboardQuery = ({ tenant_id, tenant_sk, case_id, contact_id, enabled = true }) => {
  return useQuery({
    queryKey: [RETAINER_BILLING_DASHBOARD_QUERY_KEY, case_id, contact_id],
    queryFn: async () => {
      const response = await getClientPortalBillingDashboard(tenant_id, tenant_sk, case_id, contact_id, 'retainer');
      return normalizeRetainerDashboardResponse(response?.data ?? {});
    },
    enabled: Boolean(enabled && tenant_id && tenant_sk && case_id && contact_id),
    suspense: true,
    useErrorBoundary: true,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
};
